import { motion } from "framer-motion";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { useTranslation } from "@/hooks/useTranslation";
import greece20NextGenEn from "@/assets/greece20-nextgen-en.png.asset.json";
import greece20NextGenEl from "@/assets/greece20-nextgen-el.png.asset.json";
import DigitalGlobeCircuitIcon from "@/components/icons/DigitalGlobeCircuitIcon";

const DigitalTransformation = () => {
  const { t, language } = useTranslation();
  const greece2Logo = language === 'el' ? greece20NextGenEl : greece20NextGenEn;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-6 relative overflow-hidden">
        <div className="container-padding relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/10 to-accent-teal/10 border border-secondary/20 rounded-full px-6 py-3 text-secondary font-medium backdrop-blur-sm mb-8" 
              initial={{ opacity: 0, scale: 0.9 }} 
              animate={{ opacity: 1, scale: 1 }} 
              transition={{ duration: 0.5 }}
            >
              <DigitalGlobeCircuitIcon className="w-5 h-5 shrink-0" />
              {t.digitalTransformation.badge}
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }} 
              className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text mb-2 font-bold text-accent-teal break-words hyphens-auto px-4"
            >
              {t.digitalTransformation.title}
            </motion.h1>
            
            <motion.p 
              className="text-xs sm:text-sm md:text-base text-muted-foreground max-w-3xl mx-auto leading-relaxed px-4" 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {t.digitalTransformation.subtitle}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="pt-2 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="bg-white/90 backdrop-blur-lg rounded-2xl p-8 md:px-12 md:pt-8 pb-4 md:pb-6 border border-secondary/20 shadow-sm">
            {/* Brief Description */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-3">
                {t.digitalTransformation.briefDescription}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t.digitalTransformation.briefDescriptionContent}
              </p>
              <p className="text-sm text-muted-foreground/70 leading-relaxed mt-3">
                {t.digitalTransformation.investmentPlanDisclaimer}
              </p>
            </div>

            {/* Investment Plan Budget */}
            <div>
              <div className="flex justify-center flex-wrap gap-x-12 gap-y-4">
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-foreground">
                    {t.digitalTransformation.investmentPlanBudget}
                  </h3>
                  <p className="text-2xl font-bold text-primary mt-2">
                    {t.digitalTransformation.investmentPlanBudgetContent}
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-foreground">
                    {t.digitalTransformation.investmentPlanEuFunding}
                  </h3>
                  <p className="text-2xl font-bold text-primary mt-2">
                    {t.digitalTransformation.investmentPlanEuFundingContent}
                  </p>
                </div>
              </div>
              <div className="flex justify-center mt-6">
              <motion.a
                href="https://greece20.gov.gr"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full max-w-2xl rounded-xl transition-all duration-300 hover:opacity-90"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <img
                  src={greece2Logo.url}
                  alt={t.digitalTransformation.logoAlt}
                  className="w-full rounded-lg object-contain"
                />
              </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
export default DigitalTransformation;